/**
 * SI (International) units for markers.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE: US conventional units are canonical. Nothing stored ever
 * changes.
 * ---------------------------------------------------------------------------
 *
 * Every number in DynamoDB — the 2,765 BloodworkRange rows across 167
 * attributes, and every marker value on every Bloodwork record — is in US
 * conventional units, and so is every threshold authored against them: the
 * glucose/adrenal/immune/anemia gauge tiers, CKD-EPI eGFR, corrected calcium,
 * free-copper %, viscosity, MVX, and every pattern cut-point sourced from the
 * Google Sheets. Converting the stored data would mean re-authoring and
 * re-validating all of it.
 *
 * So conversion happens at exactly two boundaries — what the practitioner
 * types, and what the report prints — and nowhere in between. Two properties
 * make that safe:
 *
 *   1. Every conversion here is a pure scale (SI = US x factor), so it is
 *      monotonic. Bar positions, percentFromMean, altChartValue, markerStatus
 *      and getSectionStatus are provably identical in both systems. Only the
 *      printed numbers and the unit labels differ.
 *   2. There is no offset anywhere in this table. HbA1c is the one marker whose
 *      SI form (mmol/mol, IFCC) needs one, and it deliberately stays in % —
 *      see SAME_IN_BOTH_SYSTEMS.
 *
 * Corollary that bites if forgotten: NEVER round on the way to canonical. A
 * practitioner typing 5.5 mmol/L must store the full 99.0813... mg/dL so it
 * redisplays as 5.5, not 5.4.
 *
 * Adding a marker? unitConversions.test.ts fails until the new PanelField id
 * appears either here or in SAME_IN_BOTH_SYSTEMS. That is deliberate — every
 * marker gets a units decision. See docs/adding-a-marker-runbook.md.
 */

export type UnitSystem = 'US' | 'SI';

export interface SiUnitSpec {
  /** Unit label shown beside the value, replacing PanelField.units. */
  label: string;
  /** Short form for chart rows, replacing PanelField.chartUnits. */
  chartLabel?: string;
  /** canonical (US) x factor = SI value. 1 means the unit is a pure relabel. */
  factor: number;
  /** Decimal places when displaying in SI. */
  precision: number;
  /** Decimal places for the greyed-out placeholder; defaults to precision. */
  placeholderPrecision?: number;
  /**
   * The marker's NAME changes in SI, not just its unit. Only BUN, which SI
   * regions report as the urea molecule rather than urea nitrogen.
   */
  labelOverride?: string;
}

/**
 * Hormone ranges are stored per menstrual phase (estradiolE2Follicular,
 * fshOvulation, progesteroneNone...) while the PanelField — and therefore the
 * entry-form state key — is the bare marker. Callers pass whichever they have,
 * so normalize before every lookup. Same for eGFR's African-American variant.
 */
const RANGE_ATTRIBUTE_SUFFIX = /(Follicular|Luteal|Ovulation|None)$/;

export function normalizeAttribute(attribute: string): string {
  if (!attribute) return attribute;
  if (attribute === 'eGFRforAA') return 'eGFR';
  return attribute.replace(RANGE_ATTRIBUTE_SUFFIX, '');
}

/**
 * Ratios are always computed from the canonical mg/dL values and shown
 * unitless, whatever the display system.
 *
 * Some are scale-invariant and some are not — Cholesterol/HDL survives the
 * switch because both sides carry the same 0.02586, but TG/HDL does not (TG is
 * 0.01129), so an SI-derived TG/HDL is ~0.44x the US one. Every optimal band
 * and every glucose pattern cut-point in this app (TG/HDL < 2, and the
 * BUN/Creatinine bands) is built on the US-derived number, so treating the
 * whole family uniformly keeps them all valid and removes the need to reason
 * case by case. The UI carries a tooltip saying so.
 */
export const US_DERIVED_RATIOS: ReadonlySet<string> = new Set([
  'albuminGlobulinRatio',
  'apoBapoA1Ratio',
  'bunCreatinineRatio',
  'cholesterolHDLRatio',
  'copperZincRatio',
  'freeT4Index',
  'nlr',
  'sodiumPotassiumRatio',
  'triglyceridesHDLRatio',
]);

/**
 * Markers whose unit string is identical in both systems, and the handful of
 * non-numeric form fields. Listed explicitly rather than inferred so the
 * coverage test can prove every marker was considered.
 */
export const SAME_IN_BOTH_SYSTEMS: ReadonlySet<string> = new Set([
  // Form plumbing, not markers.
  'clientId',
  'collectionDate',
  'menstrualPhase',
  'wbcDifferentialUnits',

  // Percentages, and the counts/indices already reported the same way.
  'basophils',
  'eosinophils',
  'hematocrit',
  'lymphocytes',
  'monocytes',
  'neutrophils',
  'percentOfFreeCopper',
  'psaFreePercent',
  'rbcDistributionWidth',
  'reticulocyteCount',
  'saturation',
  't3Uptake',
  'testosteroneBioavailablePercent',
  'RPI',

  // Femtolitres and picograms are SI already.
  'meanCorpuscularHGB',
  'meanCorpuscularVolume',
  'meanPlateletVolume',

  // Enzyme activity is U/L worldwide.
  'alkalinePhosphatase',
  'alt',
  'amylase',
  'ast',
  'ggt',
  'lactateDehydrogenase',

  // Already stored in SI units — no US conventional form to convert from.
  'anionGap',
  'anionGapWithoutK',
  'bileAcids',
  'carbonDioxide',
  'chloride',
  'homocysteine',
  'potassium',
  'shbg',
  'sodium',

  // Reported identically in both systems.
  'dDimer',
  'eGFR',
  'eGFRforAA',
  'esr',
  'hsCRP',
  'viscosityCapiliaries',

  // HbA1c stays %. Canada reports % natively, Australia reports % alongside
  // mmol/mol, and every glucose pattern threshold in this app is written in %.
  // Switching to IFCC mmol/mol would need an affine converter AND a rewrite of
  // every glucose insight that names a percentage. Australia's dual display is
  // a Phase 2 decision.
  'hemoglobinA1c',

  // Lp(a) mg/dL <-> nmol/L is assay-dependent, not a fixed factor — the mass of
  // an apo(a) particle varies with its isoform. Already stored as nmol/L here.
  'lipoproteinA',

  // Total estrogens is a mixture (E1 + E2 + E3), so it has no single molar
  // mass. Labs report the mass concentration in both systems.
  'estrogensTotal',

  // MVX POC markers — already SI where it matters, and the section is on hold.
  'citrate',
  'glycA',
  'isoleucine',
  'leucine',
  'mmx',
  'mvx',
  'ivx',
  'sHdlP',
  'valine',

  // I-cubed questionnaire scores, not measurements.
  'i3Biomarkers',
  'i3EnergyFatigue',
  'i3GutDigestive',
  'i3Hormonal',
  'i3InflammationPain',
  'i3Infections',
  'i3Musculoskeletal',
  'i3NeuroImmune',
  'i3Sensitivities',
  'i3SkinRespiratory',
]);

/**
 * canonical (US) x factor = SI. Keyed by PanelField id.
 *
 * A factor of 1 is a pure relabel — the number is unchanged and only the unit
 * string differs. Those are cheap to ship and carry a lot of the perceived
 * correctness for Canada and Australia, where "k/mcL" simply is not how a
 * blood count is written.
 */
export const SI_UNITS: Readonly<Record<string, SiUnitSpec>> = {
  // ----- CBC: all relabels -------------------------------------------------
  whiteBloodCells: { label: 'x10E9/L', chartLabel: 'x10E9/L', factor: 1, precision: 2, placeholderPrecision: 1 },
  redBloodCells: { label: 'x10E12/L', chartLabel: 'x10E12/L', factor: 1, precision: 2 },
  plateletCount: { label: 'x10E9/L', chartLabel: 'x10E9/L', factor: 1, precision: 2 },
  neutrophilsAbsolute: { label: 'x10E9/L', factor: 1, precision: 3 },
  lymphocytesAbsolute: { label: 'x10E9/L', factor: 1, precision: 3 },
  monocytesAbsolute: { label: 'x10E9/L', factor: 1, precision: 3 },
  eosinophilsAbsolute: { label: 'x10E9/L', factor: 1, precision: 3 },
  basophilsAbsolute: { label: 'x10E9/L', factor: 1, precision: 3 },

  // ----- Haemoglobin and protein: g/dL -> g/L ------------------------------
  hemoglobin: { label: 'g/L', factor: 10, precision: 0 },
  mchConcentration: { label: 'g/L', factor: 10, precision: 0 },
  protein: { label: 'g/L', factor: 10, precision: 0 },
  albumin: { label: 'g/L', factor: 10, precision: 0 },
  globulin: { label: 'g/L', factor: 10, precision: 0 },

  // ----- Metabolic ---------------------------------------------------------
  glucose: { label: 'mmol/L', factor: 0.05551, precision: 1 },
  insulin: { label: 'pmol/L', factor: 6.945, precision: 0 },
  cpeptide: { label: 'nmol/L', factor: 0.331, precision: 2 },

  // ----- Kidney ------------------------------------------------------------
  /**
   * BUN is not merely a unit change. SI regions assay and report the whole
   * urea molecule, not the nitrogen fraction of it, so both the number AND the
   * marker's name change: 1 mg/dL of urea nitrogen is 0.357 mmol/L of urea.
   * Canada and Australia both report "Urea". Hence labelOverride.
   */
  bun: { label: 'mmol/L', factor: 0.357, precision: 1, labelOverride: 'Urea' },
  creatinine: { label: 'μmol/L', factor: 88.4, precision: 0 },
  uricAcid: { label: 'μmol/L', factor: 59.48, precision: 0 },

  // ----- Minerals ----------------------------------------------------------
  calcium: { label: 'mmol/L', factor: 0.2495, precision: 2 },
  phosphorus: { label: 'mmol/L', factor: 0.3229, precision: 2 },
  magnesium: { label: 'mmol/L', factor: 0.4114, precision: 2 },
  magnesiumRBC: { label: 'mmol/L', factor: 0.4114, precision: 2 },

  // ----- Lipids ------------------------------------------------------------
  cholesterol: { label: 'mmol/L', factor: 0.02586, precision: 2 },
  hdl: { label: 'mmol/L', factor: 0.02586, precision: 2 },
  ldl: { label: 'mmol/L', factor: 0.02586, precision: 2 },
  // Triglycerides carry a different factor from cholesterol — this is exactly
  // why TG/HDL is in US_DERIVED_RATIOS.
  triglycerides: { label: 'mmol/L', factor: 0.01129, precision: 2 },
  apolipoproteinA1: { label: 'g/L', factor: 0.01, precision: 2 },
  apolipoproteinB: { label: 'g/L', factor: 0.01, precision: 2 },

  // ----- Liver / bilirubin -------------------------------------------------
  bilirubin: { label: 'μmol/L', factor: 17.1, precision: 0 },
  bilirubinDirect: { label: 'μmol/L', factor: 17.1, precision: 0 },
  bilirubinIndirect: { label: 'μmol/L', factor: 17.1, precision: 0 },

  // ----- Iron studies ------------------------------------------------------
  iron: { label: 'μmol/L', factor: 0.1791, precision: 1 },
  tibc: { label: 'μmol/L', factor: 0.1791, precision: 1 },
  ferritin: { label: 'μg/L', factor: 1, precision: 2 },
  transferrin: { label: 'g/L', factor: 0.01, precision: 2 },

  // ----- Inflammation / coagulation ---------------------------------------
  fibrinogen: { label: 'g/L', factor: 0.01, precision: 2 },

  // ----- Copper and zinc ---------------------------------------------------
  // Each mass-unit variant is its own attribute here (the practitioner picks
  // the one their lab used), so each needs its own factor into umol/L.
  ceruloplasmin: { label: 'g/L', factor: 0.01, precision: 2 },
  // NOTE: these four ids carry a Greek small mu (U+03BC), matching PanelField.
  copperSerumμgdL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 0.1574, precision: 1 },
  copperSerummcgmL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 15.74, precision: 1 },
  copperRBCμgdL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 0.1574, precision: 1 },
  copperRBCμgmL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 15.74, precision: 1 },
  zincSerumμgdL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 0.153, precision: 1 },
  zincSerummcgmL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 15.3, precision: 1 },
  zincRBCμgdL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 0.153, precision: 1 },
  zincRBCmgL: { label: 'μmol/L', chartLabel: 'μmol/L', factor: 15.3, precision: 1 },

  // ----- Vitamins ----------------------------------------------------------
  vitaminA: { label: 'μmol/L', factor: 0.03491, precision: 2 },
  vitaminB6: { label: 'nmol/L', factor: 4.046, precision: 1 },
  vitaminB12: { label: 'pmol/L', factor: 0.7378, precision: 0 },
  vitaminD: { label: 'nmol/L', factor: 2.496, precision: 0 },
  vitaminD125: { label: 'pmol/L', factor: 2.4, precision: 0 },
  folateSerum: { label: 'nmol/L', factor: 2.266, precision: 1 },
  folateRBC: { label: 'nmol/L', factor: 2.266, precision: 1 },

  // ----- Thyroid -----------------------------------------------------------
  // TSH is already dual-labelled "uIU/mL (mIU/L)" in PanelField; SI drops the
  // US half. Numerically identical.
  tsh: { label: 'mIU/L', chartLabel: 'mIU/L', factor: 1, precision: 3 },
  t4Free: { label: 'pmol/L', factor: 12.87, precision: 1 },
  t3Free: { label: 'pmol/L', factor: 1.536, precision: 1 },
  t4Total: { label: 'nmol/L', factor: 12.87, precision: 0 },
  t3Total: { label: 'nmol/L', factor: 0.01536, precision: 1 },
  reverseT3: { label: 'nmol/L', factor: 0.01536, precision: 2 },
  tpoAb: { label: 'kIU/L', factor: 1, precision: 2 },
  tgAb: { label: 'kIU/L', factor: 1, precision: 2 },
  tbg: { label: 'mg/L', factor: 1, precision: 2 },

  // ----- Adrenal / sex hormones -------------------------------------------
  cortisolAM: { label: 'nmol/L', factor: 27.59, precision: 0 },
  cortisolPM: { label: 'nmol/L', factor: 27.59, precision: 0 },
  dheaS: { label: 'μmol/L', factor: 0.02714, precision: 1 },
  pregnenolone: { label: 'nmol/L', factor: 0.03156, precision: 2 },
  progesterone: { label: 'nmol/L', factor: 3.18, precision: 1 },
  hydroxyprogesterone17: { label: 'nmol/L', factor: 0.03026, precision: 1 },
  testosteroneTotal: { label: 'nmol/L', factor: 0.03467, precision: 1 },
  testosteroneTotalLCMS: { label: 'nmol/L', factor: 0.03467, precision: 1 },
  testosteroneBioavailable: { label: 'nmol/L', factor: 0.03467, precision: 1 },
  testosteroneFreeDirect: { label: 'pmol/L', factor: 3.467, precision: 0 },
  testosteroneFreeCalc: { label: 'pmol/L', factor: 3.467, precision: 0 },
  dihydrotestosterone: { label: 'nmol/L', factor: 0.03444, precision: 1 },
  estradiolE2: { label: 'pmol/L', factor: 3.671, precision: 0 },
  estroneE1: { label: 'pmol/L', factor: 3.698, precision: 0 },
  estriolE3: { label: 'nmol/L', factor: 3.467, precision: 2 },
  antiMullerianHormone: { label: 'pmol/L', factor: 7.14, precision: 1 },
  igf1: { label: 'μg/L', factor: 1, precision: 1 },
  // mIU/mL and IU/L are the same magnitude; SI regions write the latter.
  fsh: { label: 'IU/L', factor: 1, precision: 2 },
  lh: { label: 'IU/L', factor: 1, precision: 2 },
  hCG: { label: 'IU/L', factor: 1, precision: 2 },
  // Prolactin's mIU/L form is assay-standardised rather than a clean molar
  // conversion, so SI regions commonly keep the mass unit as ug/L.
  prolactin: { label: 'μg/L', factor: 1, precision: 2 },
  prolactinLactating: { label: 'μg/L', factor: 1, precision: 2 },

  // ----- Prostate ----------------------------------------------------------
  psaTotal: { label: 'μg/L', factor: 1, precision: 2 },
  psaFree: { label: 'μg/L', factor: 1, precision: 3 },
};

/** True when the marker's displayed number is identical in both systems. */
export function isSameInBothSystems(attribute: string): boolean {
  const key = normalizeAttribute(attribute);
  return !SI_UNITS[key] || SI_UNITS[key].factor === 1;
}

/**
 * Canonical (US) -> the value to display. Deliberately unrounded; rounding is
 * the caller's job at display time, via precisionFor().
 */
export function toDisplayValue(
  attribute: string,
  canonical: number | undefined | null,
  system: UnitSystem,
): number | undefined {
  if (canonical == null || (canonical as any) === '' || isNaN(Number(canonical))) {
    return undefined;
  }
  const value = Number(canonical);
  if (system !== 'SI') return value;
  const key = normalizeAttribute(attribute);
  // Ratios are always US-derived, so they pass straight through.
  if (US_DERIVED_RATIOS.has(key)) return value;
  const spec = SI_UNITS[key];
  return spec ? value * spec.factor : value;
}

/**
 * What the practitioner typed -> canonical (US), for storage and for every
 * calculation. Unrounded, so a typed SI value round-trips exactly.
 */
export function toCanonicalValue(
  attribute: string,
  displayed: number | undefined | null,
  system: UnitSystem,
): number | undefined {
  if (displayed == null || (displayed as any) === '' || isNaN(Number(displayed))) {
    return undefined;
  }
  const value = Number(displayed);
  if (system !== 'SI') return value;
  const key = normalizeAttribute(attribute);
  if (US_DERIVED_RATIOS.has(key)) return value;
  const spec = SI_UNITS[key];
  return spec ? value / spec.factor : value;
}

/** Decimal places to display this marker at, in the given system. */
export function precisionFor(
  attribute: string,
  system: UnitSystem,
  usPrecision?: number,
): number {
  const fallback = usPrecision != null ? usPrecision : 2;
  if (system !== 'SI') return fallback;
  const spec = SI_UNITS[normalizeAttribute(attribute)];
  return spec ? spec.precision : fallback;
}

/** Placeholder decimal places, which some markers keep coarser than the value. */
export function placeholderPrecisionFor(
  attribute: string,
  system: UnitSystem,
  usPlaceholderPrecision?: number,
): number {
  if (system !== 'SI') {
    return usPlaceholderPrecision != null ? usPlaceholderPrecision : 2;
  }
  const spec = SI_UNITS[normalizeAttribute(attribute)];
  if (!spec) {
    return usPlaceholderPrecision != null ? usPlaceholderPrecision : 2;
  }
  return spec.placeholderPrecision != null
    ? spec.placeholderPrecision
    : spec.precision;
}

/**
 * Unit label for the given system. `usUnits` is the PanelField value to fall
 * back on, so a marker with no SI entry keeps whatever it already showed.
 */
export function unitsFor(
  attribute: string,
  system: UnitSystem,
  usUnits?: string,
  chart?: boolean,
): string | undefined {
  if (system !== 'SI') return usUnits;
  const spec = SI_UNITS[normalizeAttribute(attribute)];
  if (!spec) return usUnits;
  return chart && spec.chartLabel ? spec.chartLabel : spec.label;
}

/**
 * The marker's own name in the given system, or undefined when it does not
 * change. Only BUN -> Urea today.
 */
export function labelOverrideFor(
  attribute: string,
  system: UnitSystem,
): string | undefined {
  if (system !== 'SI') return undefined;
  return SI_UNITS[normalizeAttribute(attribute)]?.labelOverride;
}
